import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, H5, M, PageSizeType } from 'design-system-web';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { Channel } from 'application-types';
import { CoverageHoursPanel } from './CoverageHoursPanel';
import { CoverageHoursTable } from './CoverageHoursTable';
import { useCoverageHoursPermissions } from './useCoverageHoursPermissions';
import { createCoverageHoursDeleteConfirmViewModel } from './coverageHoursDeleteConfirm';
import { useCoverageHoursOptimisticRows } from './useCoverageHoursOptimisticRows';
import { useDeleteCoverageHours } from './useDeleteCoverageHours';
import {
  COVERAGE_HOURS_PAGE_SIZE,
  CoverageHoursRouting,
  useCoverageHours,
} from './useCoverageHours';
import styles from './CoverageHours.scss';

type Props = {
  channel?: Channel;
  categories?: WorkOrderModuleCategoriesSettings[];
};

export function CoverageHours({ channel, categories = [] }: Props) {
  const { t } = useTranslation();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(
    COVERAGE_HOURS_PAGE_SIZE
  );
  const { canManage } = useCoverageHoursPermissions({
    channelId: channel?._id,
  });

  const {
    routings,
    totalRows,
    loading,
    error,
    fetchCoverageHours,
    refreshCoverageHours,
  } = useCoverageHours({
    channelId: channel?._id ?? '',
    pageIndex: page,
    perPage: pageSize,
  });

  useEffect(() => {
    if (channel?._id) {
      fetchCoverageHours();
    }
  }, [channel?._id, fetchCoverageHours]);

  const handleOpenPanel = () => {
    if (!channel?._id) {
      window.Toast.show(
        t(
          'web.admin.serviceRequest.settings.coverageHours.create.channelError'
        )
      );

      return;
    }

    setIsPanelOpen(true);
  };

  const handleClosePanel = () => setIsPanelOpen(false);

  const handlePageSizeChange = (nextPageSize: PageSizeType) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const {
    displayedRoutings,
    onOptimisticDelete,
    onDeleteError,
    onDeleteSuccess,
  } = useCoverageHoursOptimisticRows({
    routings,
    page,
    setPage,
    refreshCoverageHours,
  });

  const { deleteCoverageHours, loading: deleting } = useDeleteCoverageHours({
    channelId: channel?._id ?? '',
  });

  const confirmAndDelete = async (routing: CoverageHoursRouting) => {
    try {
      await window.Alert.confirm(
        createCoverageHoursDeleteConfirmViewModel(routing, t)
      );
    } catch {
      return;
    }

    onOptimisticDelete(routing.routingId);

    try {
      await deleteCoverageHours(routing.routingId);
    } catch {
      onDeleteError(routing.routingId);
      window.Toast.show(
        t('web.admin.serviceRequest.settings.coverageHours.delete.error')
      );

      return;
    }

    window.Toast.show(
      t('web.admin.serviceRequest.settings.coverageHours.toast.deleted')
    );

    await onDeleteSuccess().catch(() => undefined);
  };

  return (
    <Flex direction="column" gap={6} className={styles.coverageHoursTab}>
      <div className={styles.sectionIntro}>
        <H5 className={styles.sectionTitle}>
          {t('web.admin.serviceRequest.settings.tabs.coverageHours')}
        </H5>
        <M className={styles.sectionDescription}>
          {t('web.admin.serviceRequest.settings.coverageHours.description')}
        </M>
      </div>
      {canManage && (
        <div className={styles.toolbar}>
          <Button
            variant="primary"
            size="large"
            testId="coverage-hours-create"
            onClick={handleOpenPanel}
          >
            {t(
              'web.admin.serviceRequest.settings.coverageHours.create.button'
            )}
          </Button>
        </div>
      )}

      {channel?._id && (
        <CoverageHoursTable
          routings={displayedRoutings}
          totalRows={totalRows}
          loading={loading}
          error={error}
          canManage={canManage}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          onDeleteRow={confirmAndDelete}
          isDeleteDisabled={deleting}
        />
      )}

      {channel?._id && (
        <CoverageHoursPanel
          isOpen={isPanelOpen}
          onClose={handleClosePanel}
          channelId={channel._id}
          channel={channel}
          categories={categories}
          onCreateSuccess={fetchCoverageHours}
        />
      )}
    </Flex>
  );
}
